import React, { useCallback, useEffect, useState } from "react";

import api, {
  clearToken,
  getToken,
  setToken,
  transactionValidationApi,
} from "./api";


const STATUS_LABELS = {
  reserved: "Reservada",
  confirmed: "Confirmada",
  completed: "Completada",
  cancelled: "Cancelada",
};


function money(cents) {
  return `$${((cents || 0) / 100).toFixed(2)}`;
}


export default function TransactionValidation({ token }) {
  const [sessionChecked, setSessionChecked] = useState(false);
  const [allyUser, setAllyUser] = useState(null);
  const [transaction, setTransaction] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  const loadTransaction = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await transactionValidationApi.validate(token);
      setTransaction(data);
    } catch (e) {
      setTransaction(null);

      if (e?.status === 404) {
        setError(
          "Esta reserva no pertenece a este establecimiento o ya no está disponible."
        );
      } else if (e?.status === 409) {
        setError(e?.message || "Esta reserva ya no puede validarse.");
      } else if (e?.status === 401) {
        setAllyUser(null);
      } else {
        setError(e?.message || "No pudimos validar esta reserva.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);


  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      if (!getToken()) {
        if (mounted) setSessionChecked(true);
        return;
      }

      try {
        const user = await api.auth.me();

        if (!mounted) return;

        if (user?.role === "ally" && user?.ally_id) {
          setAllyUser(user);
        } else {
          setError(
            "La sesión actual no corresponde a una cuenta de aliado PawCita."
          );
        }
      } catch {
        clearToken();
      } finally {
        if (mounted) setSessionChecked(true);
      }
    }

    restoreSession();

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    if (sessionChecked && allyUser) {
      loadTransaction();
    }
  }, [sessionChecked, allyUser, loadTransaction]);


  async function handleLogin(e) {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await api.auth.login({
        email: email.trim(),
        password,
      });

      if (res?.user?.role !== "ally" || !res?.user?.ally_id) {
        clearToken();
        setAllyUser(null);
        setError(
          "Esta cuenta no está autorizada como aliado PawCita."
        );
        return;
      }

      setToken(res.token);
      setAllyUser(res.user);
    } catch (e2) {
      setError(e2?.message || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  }


  async function handleComplete() {
    if (!transaction?.can_complete || completing) return;

    setCompleting(true);
    setError("");
    setSuccess("");

    try {
      const updated = await transactionValidationApi.complete(token);
      setTransaction({
        ...updated,
        can_complete: false,
      });
      setSuccess("Servicio confirmado correctamente.");
    } catch (e) {
      setError(e?.message || "No pudimos confirmar el servicio.");
    } finally {
      setCompleting(false);
    }
  }


  function switchAccount() {
    clearToken();
    setAllyUser(null);
    setTransaction(null);
    setSuccess("");
    setError("");
    setPassword("");
  }

  function goToAllyPanel() {
    window.location.href = "/aliados";
  }


  if (!sessionChecked) {
    return (
      <Page>
        <Card>
          <p className="text-center text-sm text-slate-500">
            Verificando acceso…
          </p>
        </Card>
      </Page>
    );
  }


  if (!allyUser) {
    return (
      <Page>
        <Card>
          <Brand />

          <h1 className="mt-5 text-center text-2xl font-black text-slate-900">
            Validar reserva
          </h1>

          <p className="mt-2 text-center text-sm leading-6 text-slate-500">
            Inicia sesión con la cuenta autorizada del establecimiento para
            validar esta reserva PawCita.
          </p>

          <form onSubmit={handleLogin} className="mt-7 space-y-4">
            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Correo
              </label>
              <input
                data-testid="transaction-ally-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-slate-400"
                placeholder="correo@establecimiento.com"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-bold text-slate-700">
                Contraseña
              </label>
              <input
                data-testid="transaction-ally-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-slate-400"
                placeholder="••••••••"
              />
            </div>

            {error ? <ErrorMessage>{error}</ErrorMessage> : null}

            <button
              data-testid="transaction-ally-login"
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-slate-900 px-5 py-3.5 font-black text-white disabled:opacity-50"
            >
              {loading ? "Ingresando…" : "Ingresar como aliado"}
            </button>
          </form>
        </Card>
      </Page>
    );
  }


  return (
    <Page>
      <Card>
        <Brand />

        <p className="mt-5 text-center text-xs font-black uppercase tracking-widest text-rose-500">
          Reserva PawCita
        </p>

        {loading && !transaction ? (
          <p className="mt-6 text-center text-sm text-slate-500">
            Validando reserva…
          </p>
        ) : null}

        {error ? (
          <div className="mt-6">
            <ErrorMessage>{error}</ErrorMessage>

            <button
              type="button"
              onClick={goToAllyPanel}
              data-testid="transaction-back-to-ally-panel"
              className="mt-4 w-full rounded-2xl bg-[#0B2D4D] px-5 py-3 font-black text-white"
            >
              Volver al Panel de Aliados
            </button>

            <button
              type="button"
              onClick={switchAccount}
              className="mt-3 w-full px-4 py-2 text-sm font-bold text-slate-500"
            >
              Usar otra cuenta
            </button>
          </div>
        ) : null}

        {transaction ? (
          <>
            <h1
              data-testid="transaction-validation-order"
              className="mt-3 text-center text-2xl font-black text-slate-900"
            >
              {transaction.order_id}
            </h1>

            <div className="mt-7 rounded-3xl border border-slate-200 bg-white p-5">
              <Row label="Aliado" value={transaction.ally_name} />
              <Row label="Servicio" value={transaction.service_name} />
              <Row label="Mascota" value={transaction.dog_name} />
              <Row
                label="Estado"
                value={STATUS_LABELS[transaction.status] || transaction.status}
              />

              <div className="my-4 border-t border-slate-100" />

              <Row
                label="Total"
                value={money(transaction.total_cents)}
                strong
              />
            </div>

            {success ? (
              <>
                <div
                  data-testid="transaction-validation-success"
                  className="mt-5 rounded-2xl bg-emerald-50 p-4 text-center text-sm font-bold text-emerald-800"
                >
                  {success}
                </div>

                <button
                  type="button"
                  onClick={goToAllyPanel}
                  data-testid="transaction-success-back-to-ally-panel"
                  className="mt-4 w-full rounded-2xl bg-[#0B2D4D] px-5 py-4 text-base font-black text-white"
                >
                  Volver al Panel de Aliados
                </button>
              </>
            ) : null}

            {transaction.can_complete ? (
              <button
                data-testid="transaction-complete"
                type="button"
                disabled={completing}
                onClick={handleComplete}
                className="mt-6 w-full rounded-2xl bg-slate-900 px-5 py-4 text-base font-black text-white disabled:opacity-50"
              >
                {completing ? "Confirmando…" : "Confirmar servicio"}
              </button>
            ) : null}

            <p className="mt-5 text-center text-xs leading-5 text-slate-500">
              Al confirmar, PawCita registrará que este establecimiento validó
              la atención correspondiente a esta reserva.
            </p>

            <button
              type="button"
              onClick={switchAccount}
              className="mt-5 w-full px-4 py-2 text-sm font-bold text-slate-500"
            >
              Usar otra cuenta
            </button>
          </>
        ) : null}
      </Card>
    </Page>
  );
}


function Page({ children }) {
  return (
    <div className="min-h-screen bg-[#FDF7EA] px-4 py-10">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}


function Card({ children }) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      {children}
    </div>
  );
}


function Brand() {
  return (
    <div className="text-center">
      <div className="text-2xl font-black tracking-tight text-slate-900">
        PawCita
      </div>
      <div className="mt-1 text-xs font-bold text-slate-400">
        Paws + Humans, Better Stories
      </div>
    </div>
  );
}


function Row({ label, value, strong = false }) {
  return (
    <div className="flex items-start justify-between gap-5 py-2">
      <span className="text-sm text-slate-500">{label}</span>
      <span
        className={`text-right text-sm ${
          strong ? "font-black text-slate-900" : "font-bold text-slate-800"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}


function ErrorMessage({ children }) {
  return (
    <div
      data-testid="transaction-validation-error"
      className="rounded-2xl bg-rose-50 p-4 text-sm font-bold leading-5 text-rose-700"
    >
      {children}
    </div>
  );
}
